import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Activity, TrendingUp, ExternalLink, Eye, ShoppingCart, Globe, Loader } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * TrafficAnalyticsCard — admin-facing dashboard card that summarises
 * recent traffic to the booking page using the in-house page_views table.
 *
 * Shows:
 *  - Top-line totals (page views, service views, booking conversions)
 *  - Conversion rate (page views → bookings)
 *  - Top viewed services
 *  - Per-service funnel (views → started → completed)
 *  - Referrer breakdown bucketed into google / facebook / instagram / etc.
 *  - Top-visited paths
 *
 * Period selector: 7 / 30 / 90 days. The backend caps at 365 days.
 */

const REFERRER_LABELS = {
  google: 'Google',
  bing: 'Bing',
  duckduckgo: 'DuckDuckGo',
  facebook: 'Facebook',
  instagram: 'Instagram',
  twitter: 'Twitter / X',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  reddit: 'Reddit',
  linkedin: 'LinkedIn',
  direct: 'Direct / Bookmark',
  other: 'Other',
};

const TrafficAnalyticsCard = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const token = sessionStorage.getItem('token');
    axios
      .get(`${API_URL}/analytics/summary?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => { if (alive) setData(res.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center text-gray-500 py-6 justify-center">
          <Loader size={18} className="animate-spin mr-2" />
          Loading traffic analytics...
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="card">
        <h2 className="text-xl font-bold mb-2 flex items-center">
          <Activity size={22} className="mr-2 text-primary-600" />
          Traffic Analytics
        </h2>
        <p className="text-sm text-gray-500">
          Could not load analytics. Make sure you're an admin and try refreshing.
        </p>
      </div>
    );
  }

  const t = data.totals || {};

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center">
          <Activity size={22} className="mr-2 text-primary-600" />
          Traffic Analytics
        </h2>
        <div className="flex items-center space-x-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1 text-sm rounded ${
                days === d
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Last {d} days
            </button>
          ))}
        </div>
      </div>

      {/* Top-line summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="p-3 bg-blue-50 border border-blue-200 rounded">
          <div className="text-xs text-blue-700 uppercase flex items-center">
            <Eye size={12} className="mr-1" /> Page Views
          </div>
          <div className="text-2xl font-bold text-blue-900">{t.page_views ?? 0}</div>
        </div>
        <div className="p-3 bg-purple-50 border border-purple-200 rounded">
          <div className="text-xs text-purple-700 uppercase">Service Views</div>
          <div className="text-2xl font-bold text-purple-900">{t.service_views ?? 0}</div>
        </div>
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded">
          <div className="text-xs text-emerald-700 uppercase flex items-center">
            <ShoppingCart size={12} className="mr-1" /> Bookings
          </div>
          <div className="text-2xl font-bold text-emerald-900">{t.booking_completed ?? 0}</div>
        </div>
        <div className="p-3 bg-amber-50 border border-amber-200 rounded">
          <div className="text-xs text-amber-700 uppercase flex items-center">
            <TrendingUp size={12} className="mr-1" /> View → Booking
          </div>
          <div className="text-2xl font-bold text-amber-900">{t.view_to_booking_rate ?? 0}%</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top services viewed */}
        <div>
          <h3 className="font-semibold text-gray-900 mb-2">Top viewed services</h3>
          {(data.top_services || []).length === 0 ? (
            <p className="text-sm text-gray-500 italic">No service views in this period yet.</p>
          ) : (
            <div className="space-y-1">
              {data.top_services.map((s) => (
                <div key={s.service_id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                  <span className="font-medium text-gray-800 truncate">{s.service_name}</span>
                  <span className="text-gray-600 ml-2">{s.views} views</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Referrer breakdown */}
        <div>
          <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
            <Globe size={14} className="mr-1" /> Where traffic came from
          </h3>
          {(data.referrers || []).length === 0 ? (
            <p className="text-sm text-gray-500 italic">No traffic recorded in this period yet.</p>
          ) : (
            <div className="space-y-1">
              {data.referrers.map((r) => {
                const total = data.referrers.reduce((s, x) => s + parseInt(x.views, 10), 0) || 1;
                const pct = Math.round((parseInt(r.views, 10) / total) * 100);
                return (
                  <div key={r.referrer_source} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                    <span className="font-medium text-gray-800">
                      {REFERRER_LABELS[r.referrer_source] || r.referrer_source}
                    </span>
                    <span className="text-gray-600 ml-2">
                      {r.views} <span className="text-xs text-gray-500">({pct}%)</span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Service funnel */}
      {(data.service_funnel || []).length > 0 && (
        <div className="mt-6">
          <h3 className="font-semibold text-gray-900 mb-2">Service funnel — view → booking</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 uppercase border-b">
                <tr>
                  <th className="text-left py-2">Service</th>
                  <th className="text-right py-2">Views</th>
                  <th className="text-right py-2">Booking started</th>
                  <th className="text-right py-2">Completed</th>
                  <th className="text-right py-2">Conversion</th>
                </tr>
              </thead>
              <tbody>
                {data.service_funnel.map((row) => {
                  const views = parseInt(row.views, 10) || 0;
                  const completed = parseInt(row.completed, 10) || 0;
                  const rate = views > 0 ? Math.round((completed / views) * 100) : 0;
                  return (
                    <tr key={row.service_id} className="border-b last:border-0">
                      <td className="py-2 font-medium text-gray-800 truncate max-w-xs">{row.service_name}</td>
                      <td className="py-2 text-right text-gray-700">{views}</td>
                      <td className="py-2 text-right text-gray-700">{row.started || 0}</td>
                      <td className="py-2 text-right text-emerald-700 font-semibold">{completed}</td>
                      <td className="py-2 text-right text-gray-600">{rate}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top paths */}
      {(data.top_paths || []).length > 0 && (
        <div className="mt-6">
          <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
            <ExternalLink size={14} className="mr-1" /> Most visited pages
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
            {data.top_paths.map((p) => (
              <div key={p.path} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                <span className="font-mono text-xs text-gray-700 truncate">{p.path}</span>
                <span className="text-gray-600 ml-2">{p.views}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="mt-6 text-xs text-gray-500">
        Tracked in-house — no third-party scripts. For GA4 / Meta Pixel paste-in,
        see the upcoming SEO &amp; tracking roadmap item.
      </p>
    </div>
  );
};

export default TrafficAnalyticsCard;
